import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import styles from './Auth.module.less';

const ThreeBackground: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    const currentMount = mountRef.current;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Scene
    const scene = new THREE.Scene();

    // Camera (full-screen quad)
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    currentMount.appendChild(renderer.domElement);

    // Colors (derived from theme primary)
    const cssPrimary =
      getComputedStyle(document.documentElement)
        .getPropertyValue('--furiflow-primary-color')
        .trim() || '#6a0dad';
    const primaryColor = new THREE.Color(cssPrimary);
    const deepColor = primaryColor.clone().multiplyScalar(0.12);
    const accentColor = primaryColor
      .clone()
      .lerp(new THREE.Color(0xffffff), 0.35)
      .multiplyScalar(0.9);

    // Full-screen shader (aurora + stars)
    const quadGeometry = new THREE.PlaneGeometry(2, 2);
    const quadMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uResolution: {
          value: new THREE.Vector2(currentMount.clientWidth, currentMount.clientHeight),
        },
        uPrimary: { value: primaryColor },
        uDeep: { value: deepColor },
        uAccent: { value: accentColor },
        uAlpha: { value: 0.9 },
        uMotion: { value: prefersReducedMotion ? 0.0 : 1.0 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;

        uniform float uTime;
        uniform vec2 uResolution;
        uniform vec3 uPrimary;
        uniform vec3 uDeep;
        uniform vec3 uAccent;
        uniform float uAlpha;
        uniform float uMotion;
        varying vec2 vUv;

        // Hash / noise helpers
        float hash21(vec2 p) {
          p = fract(p * vec2(123.34, 345.45));
          p += dot(p, p + 34.345);
          return fract(p.x * p.y);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash21(i);
          float b = hash21(i + vec2(1.0, 0.0));
          float c = hash21(i + vec2(0.0, 1.0));
          float d = hash21(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }

        float fbm(vec2 p) {
          float v = 0.0;
          float a = 0.55;
          mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
          for (int i = 0; i < 5; i++) {
            v += a * noise(p);
            p = m * p;
            a *= 0.5;
          }
          return v;
        }

        float starField(vec2 uv, float t) {
          // grid-based pseudo-random stars (cheap and stable)
          vec2 grid = vec2(140.0, 90.0);
          vec2 gv = fract(uv * grid);
          vec2 id = floor(uv * grid);

          float rnd = hash21(id);
          float starMask = step(0.992, rnd);
          float d = length(gv - 0.5);

          float twinkle = 0.55 + 0.45 * sin(t * 2.0 + rnd * 6.2831);
          float intensity = starMask * smoothstep(0.22, 0.0, d) * twinkle;
          return intensity;
        }

        void main() {
          vec2 uv = vUv;
          vec2 px = (uv - 0.5) * vec2(uResolution.x / max(uResolution.y, 1.0), 1.0);
          float t = uTime * (0.25 + 0.75 * uMotion);

          // Base gradient
          float grad = smoothstep(-0.6, 0.9, px.y);
          vec3 col = mix(uDeep, uPrimary, grad * 0.85);

          // Subtle vignette
          float vignette = smoothstep(1.25, 0.15, length(px));
          col *= vignette;

          // Aurora ribbons
          vec2 p = px;
          p.x += 0.08 * sin(p.y * 2.2 + t);
          float n = fbm(p * 2.2 + vec2(0.0, t * 0.4));

          float ribbon1 = smoothstep(0.55, 0.92, n) * smoothstep(-0.25, 0.55, px.y);
          float ribbon2 = smoothstep(0.62, 0.96, fbm(p * 3.0 + vec2(2.0, -t * 0.35))) *
                          smoothstep(-0.35, 0.35, px.y);

          float aurora = clamp(ribbon1 * 0.9 + ribbon2 * 0.7, 0.0, 1.0);
          vec3 auroraCol = mix(uAccent, vec3(1.0), 0.15);
          col = mix(col, col + auroraCol * aurora, 0.85);

          // Stars on top (dimmer near bottom)
          float stars = starField(uv, t);
          stars *= smoothstep(0.05, 0.65, uv.y);
          col += vec3(1.0) * stars * 0.9;

          // Soft film-like noise
          float grain = (hash21(uv * uResolution + fract(t)) - 0.5) * 0.035;
          col += grain;

          gl_FragColor = vec4(col, uAlpha);
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    const quadMesh = new THREE.Mesh(quadGeometry, quadMaterial);
    scene.add(quadMesh);

    // Animation
    const clock = new THREE.Clock();
    let rafId: number | null = null;
    let isVisible = true;

    const renderFrame = () => {
      if (!isVisible) return;
      const elapsedTime = clock.getElapsedTime();
      quadMaterial.uniforms.uTime.value = elapsedTime;
      renderer.render(scene, camera);
      rafId = window.requestAnimationFrame(renderFrame);
    };

    if (prefersReducedMotion) {
      renderer.render(scene, camera);
    } else {
      renderFrame();
    }

    // Handle resize
    const handleResize = () => {
      if (currentMount) {
        renderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        quadMaterial.uniforms.uResolution.value.set(
          currentMount.clientWidth,
          currentMount.clientHeight,
        );
        if (prefersReducedMotion) {
          renderer.render(scene, camera);
        }
      }
    };

    const handleVisibilityChange = () => {
      isVisible = document.visibilityState === 'visible';
      if (isVisible && !prefersReducedMotion && rafId === null) {
        clock.getDelta();
        renderFrame();
      }
      if (!isVisible && rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (currentMount) {
        currentMount.removeChild(renderer.domElement);
      }

      quadGeometry.dispose();
      quadMaterial.dispose();
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} className={styles['three-background']} />;
};

export default ThreeBackground;
